import { useState } from "react";
import { auth, db } from "./firebase";
import {
    signInWithPopup,
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import GoogleLogo from "./assets/google.png";
import { Link } from "react-router-dom";
import logo from './logo.jpg';

const provider = new GoogleAuthProvider();

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isRegistering, setIsRegistering] = useState(false);
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        setError("");
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            
            // Check if user already exists
            const userDoc = await getDoc(doc(db, "users", user.uid));
            if (!userDoc.exists()) {
                await setDoc(doc(db, "users", user.uid), {
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    createdAt: new Date().toISOString()
                });
            }
        } catch (err) {
            console.error("Google login error:", err);
            setError(err.message || "Google login failed. Try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (isRegistering) {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;
                await setDoc(doc(db, "users", user.uid), {
                    email: user.email,
                });
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-blue-100 to-indigo-100">
            {/* Left Side - Branding */}
            <div className="w-full md:w-1/2 bg-gradient-to-br from-indigo-600 to-blue-600 flex flex-col justify-center items-center p-8 text-white">
                <img src={logo} alt="Miles2Go Logo" className="w-20 h-20 rounded-full mb-6 shadow-lg" />
                <h1 className="text-4xl font-bold mb-2">Miles2Go</h1>
                <p className="text-lg text-blue-100">Helping you travel smarter</p>
            </div>

            {/* Right Side - Login Card */}
            <div className="w-full md:w-1/2 flex items-center justify-center p-6">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>
                    )}

                    {/* Google Login */}
                    <button
                        onClick={handleGoogleLogin}
                        disabled={isLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-3 transition-all mb-6"
                    >
                        <img src={GoogleLogo} alt="Google" className="w-5 h-5" />
                        Continue with Google
                    </button>

                    {/* Divider */}
                    <div className="or-divider mb-6 flex items-center text-gray-400">
                        <hr className="flex-grow border-t border-gray-300" />
                        <span className="mx-3">OR</span>
                        <hr className="flex-grow border-t border-gray-300" />
                    </div>

                    {/* Email Login/Register Form */}
                    <form onSubmit={handleEmailAuth} className="space-y-4">
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg transition-all"
                        >
                            {isLoading ? "Processing..." : isRegistering ? "Create Account" : "Sign In"}
                        </button>
                    </form>

                    {/* Toggle Registration */}
                    <div className="mt-6 text-center text-sm text-gray-600">
                        <button
                            onClick={() => setIsRegistering(!isRegistering)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                        >
                            {isRegistering ? "Already have an account? Sign In" : "Don't have an account? Register"}
                        </button>
                    </div>

                    {/* Contact Us Link */}
                    <div className="mt-4 text-center text-sm text-gray-600">
                        <Link to="/contact" className="hover:text-gray-800 transition-colors">
                            Contact Us
                        </Link>
                    </div>

                    <div className="mt-2 text-center text-sm text-gray-600">
                        <Link to="/terms" className="hover:text-gray-800 transition-colors">
                            Terms & Conditions
                        </Link>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default Login;
